// Utilities for detecting unsaved changes across forms using a simple field schema

export type FieldType = 'string' | 'number' | 'date' | 'boolean' | 'id';
export type FieldSchema = Record<string, FieldType>;

// Normalize arbitrary values according to schema to enable stable comparison
export function normalizeValues(values: Record<string, unknown>, schema: FieldSchema) {
	const out: Record<string, unknown> = {};
	for (const [key, type] of Object.entries(schema)) {
		const raw = values?.[key];
		switch (type) {
			case 'string': {
				const s = raw == null ? '' : String(raw);
				out[key] = s.trim();
				break;
			}
			case 'id': {
				const s = raw == null || raw === '' ? null : String(raw);
				out[key] = s;
				break;
			}
			case 'number': {
				if (raw == null || raw === '') {
					out[key] = undefined;
				} else if (typeof raw === 'number') {
					out[key] = Number.isFinite(raw) ? raw : undefined;
				} else {
					const n = Number(String(raw));
					out[key] = Number.isFinite(n) ? n : undefined;
				}
				break;
			}
			case 'boolean': {
				if (typeof raw === 'boolean') out[key] = raw;
				else if (raw == null || raw === '') out[key] = false;
				else out[key] = String(raw).toLowerCase() === 'true';
				break;
			}
			case 'date': {
				if (raw == null || raw === '') {
					out[key] = null;
				} else if (raw instanceof Date) {
					out[key] = Number.isFinite(raw.getTime()) ? raw.getTime() : null;
				} else if (typeof raw === 'number') {
					out[key] = Number.isFinite(raw) ? raw : null;
				} else {
					const t = Date.parse(String(raw));
					out[key] = Number.isFinite(t) ? t : null;
				}
				break;
			}
			default:
				out[key] = raw;
		}
	}
	return out;
}

export function buildSignature(
	values: Record<string, unknown>,
	schema: FieldSchema,
): string {
	return JSON.stringify(normalizeValues(values, schema));
}

export function hasUnsavedChanges(
	currentValues: Record<string, unknown>,
	lastSavedValues: Record<string, unknown>,
	schema: FieldSchema,
): boolean {
	const cur = buildSignature(currentValues, schema);
	const last = buildSignature(lastSavedValues, schema);
	return cur !== last;
}

